import { useEffect, useMemo, useState } from "react";
import { api, formatZAR, formatInt, formatApiError } from "@/lib/api";
import { PageHeader, KpiCard } from "@/components/Kpi";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AlertTriangle, PackageX, Boxes, Ban, ArrowDownToLine, MoonStar, CheckCircle2, Search, ArrowUpDown, Truck, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function InfoIcon({ children, testid }) {
    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button type="button" data-testid={testid} className="inline-flex align-middle text-[color:var(--bl-text-mute)] hover:text-[color:var(--bl-takealot-blue)] ml-1 -mt-0.5">
                        <Info className="w-3 h-3" />
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs bg-[color:var(--bl-text)] text-white text-xs px-3 py-2 rounded-sm shadow-lg font-normal">
                    {children}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

const STATUS_META = {
    oos: { label: "Out", color: "var(--bl-danger)", bg: "rgba(192,57,43,0.12)", icon: PackageX },
    low: { label: "Low", color: "var(--bl-warning)", bg: "rgba(184,118,29,0.12)", icon: AlertTriangle },
    over: { label: "Over", color: "var(--bl-takealot-orange)", bg: "rgba(227,90,0,0.12)", icon: Boxes },
    ok: { label: "OK", color: "var(--bl-success)", bg: "rgba(47,122,77,0.10)", icon: CheckCircle2 },
    idle: { label: "Idle", color: "var(--bl-text-mute)", bg: "rgba(158,138,115,0.12)", icon: MoonStar },
    stale: { label: "Stale", color: "var(--bl-text-dim)", bg: "rgba(117,97,79,0.12)", icon: MoonStar },
};

function formatKg(v) {
    if (v == null || isNaN(v) || v === 0) return "—";
    return `${Number(v).toLocaleString("en-ZA", { maximumFractionDigits: 1 })} kg`;
}

function RegionCell({ region, data }) {
    if (!data) return <td className="px-2 py-2 text-center text-[color:var(--bl-text-mute)] text-xs">—</td>;
    const meta = STATUS_META[data.status] || STATUS_META.ok;
    const inTransit = data.in_transit || 0;
    const inReceiving = data.in_receiving || 0;
    const source = data.in_transit_source || "none";
    return (
        <td className="px-2 py-2 align-top" style={{ backgroundColor: meta.bg, borderLeft: `3px solid ${meta.color}` }} data-testid={`cell-${region}`}>
            <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono-num text-base font-medium" style={{ color: meta.color }}>{formatInt(data.qty)}</span>
                <span className="text-[10px] uppercase tracking-wider font-medium" style={{ color: meta.color }}>{meta.label}</span>
            </div>
            {inReceiving > 0 && (
                <div className="text-[10px] mt-0.5 inline-flex items-center gap-0.5 font-mono-num font-medium text-[color:var(--bl-warning)]">
                    <ArrowDownToLine className="w-3 h-3" /> +{formatInt(inReceiving)} in receiving
                    <InfoIcon testid={`info-receiving-${region}`}>
                        Stock physically at the {region} DC but not yet booked into sellable inventory. Counted toward effective stock so we do not double-order.
                    </InfoIcon>
                </div>
            )}
            {inTransit > 0 && (
                <div className="text-[10px] mt-0.5 inline-flex items-center gap-0.5 font-mono-num font-medium text-[color:var(--bl-takealot-blue)]" data-testid={`in-transit-${region}`}>
                    <Truck className="w-3 h-3" /> +{formatInt(inTransit)} on way
                    <InfoIcon testid={`info-transit-${region}`}>
                        Sent to the {region} DC but not yet received.
                        {source === "takealot" && " Source: Takealot's own stock_on_way figure per DC."}
                        {source === "shipments" && " Source: your open shipments (Takealot's number was 0, so we fell back to your Purchase Orders)."}
                    </InfoIcon>
                </div>
            )}
            <div className="text-[10px] text-[color:var(--bl-text-dim)] font-mono-num mt-0.5 flex justify-between">
                <span>sold30: {data.sold30}</span>
                <span>{data.cover_days}d cover</span>
            </div>
            {data.recommended_order > 0 && (
                <div className="text-[10px] mt-1 inline-flex items-center gap-0.5 font-mono-num font-semibold" style={{ color: meta.color }} data-testid={`reorder-${region}`}>
                    <ArrowDownToLine className="w-3 h-3" /> Order {data.recommended_order}
                    <InfoIcon testid={`info-reorder-${region}`}>
                        Target = sold30 × 1.5 = {Math.round(data.sold30 * 1.5)} units (45-day cover).<br />
                        Effective stock = on-hand ({data.qty}) + in-receiving ({inReceiving}) + in-transit ({inTransit}) = {data.effective_qty}.<br />
                        Reorder = target − effective. Only shown when effective &lt; 50% of 30-day sell-through.
                    </InfoIcon>
                </div>
            )}
        </td>
    );
}

export default function Inventory() {
    const [data, setData] = useState({ items: [], by_region: [], regions: [] });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [filterStatus, setFilterStatus] = useState(""); // '', oos, low, over, ok
    const [sortKey, setSortKey] = useState("warning"); // warning, sold30, qty, name

    useEffect(() => {
        setLoading(true);
        api.get("/inventory/matrix")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        let rows = data.items;
        if (q.trim()) {
            const term = q.toLowerCase();
            rows = rows.filter((r) => r.title.toLowerCase().includes(term) || r.sku.toLowerCase().includes(term));
        }
        if (filterStatus) {
            rows = rows.filter((r) => Object.values(r.regions).some((reg) => reg.status === filterStatus));
        }
        const sorted = [...rows];
        if (sortKey === "warning") sorted.sort((a, b) => b.warning_count - a.warning_count || b.total_sold30 - a.total_sold30);
        if (sortKey === "sold30") sorted.sort((a, b) => b.total_sold30 - a.total_sold30);
        if (sortKey === "qty") sorted.sort((a, b) => b.total_qty - a.total_qty);
        if (sortKey === "name") sorted.sort((a, b) => a.title.localeCompare(b.title));
        return sorted;
    }, [data.items, q, filterStatus, sortKey]);

    const totalStockValue = data.by_region.reduce((a, b) => a + (b.value || 0), 0);
    const totalKg = data.by_region.reduce((a, b) => a + (b.kg || 0), 0);
    const totalOos = data.by_region.reduce((a, b) => a + (b.oos || 0), 0);
    const totalLow = data.by_region.reduce((a, b) => a + (b.low || 0), 0);
    const totalOver = data.by_region.reduce((a, b) => a + (b.over || 0), 0);
    const totalSold30 = data.by_region.reduce((a, b) => a + (b.sold30 || 0), 0);
    const transit = data.in_transit || { total_qty: 0, by_region: {}, shipments: [] };
    const totalTransitKg = data.items.reduce((a, r) => a + (r.total_in_transit_kg || 0), 0);

    if (loading) return <div className="text-[color:var(--bl-text-dim)]">Analysing stock…</div>;

    return (
        <div>
            <PageHeader title="Inventory" subtitle="Stock by product and region — with reorder recommendations">
                <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--bl-text-dim)]" />
                    <input data-testid="inv-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product or SKU…"
                        className="bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] pl-8 pr-3 py-1.5 text-sm rounded-sm w-60 focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                </div>
            </PageHeader>

            <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3 mb-5">
                <KpiCard label="Total stock" value={formatInt(data.by_region.reduce((a, b) => a + (b.qty || 0), 0))} sub={`${formatKg(totalKg)} · ${data.items.length} SKUs`} testid="inv-total-qty"
                    info={<InfoIcon testid="info-total-qty">Sellable units currently at Takealot DCs across all regions. Does not include stock in receiving or on the way — see those next to it.</InfoIcon>} />
                <KpiCard label="On their way" value={formatInt(transit.total_qty)} sub={totalTransitKg > 0 ? `${formatKg(totalTransitKg)} · ${transit.shipments.length} shipments` : `${transit.shipments.length} shipments`} testid="inv-in-transit"
                    info={<InfoIcon testid="info-in-transit">Stock sent from you to Takealot that has not yet been received. Preferring Takealot's own stock_on_way per DC, with your open shipments as a fallback. This is subtracted from any reorder recommendations so you do not double-send.</InfoIcon>} />
                <KpiCard label="Stock value" value={formatZAR(totalStockValue)} sub="at selling price" testid="inv-total-value"
                    info={<InfoIcon testid="info-value">Sum of on-hand units × current selling price across every DC. Tells you how much cash is tied up in stock right now.</InfoIcon>} />
                <KpiCard label="Sold last 30d" value={formatInt(totalSold30)} sub="across all regions" accent="coffee" testid="inv-total-sold30"
                    info={<InfoIcon testid="info-sold30">Rolling 30-day units sold, aggregated from Takealot's per-DC quantity_sold_30_days figure.</InfoIcon>} />
                <KpiCard label="Out of stock" value={formatInt(totalOos)} sub="region × SKU cells" accent="orange" testid="inv-total-oos"
                    info={<InfoIcon testid="info-oos">Number of (region × SKU) combinations where effective stock is zero but there are recent sales. These are urgent reorders.</InfoIcon>} />
                <KpiCard label="Low stock" value={formatInt(totalLow)} sub={`Over: ${totalOver}`} accent="orange" testid="inv-total-low"
                    info={<InfoIcon testid="info-low">Cells where effective stock is below 50 % of the 30-day sell-through. "Over" is the count with more than 3× cover — potential storage-fee risk.</InfoIcon>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5" data-testid="inv-by-region">
                {data.by_region.map((r) => (
                    <div key={r.region} className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-4 rounded-sm shadow-sm" data-testid={`inv-region-${r.region}`}>
                        <div className="flex items-baseline justify-between">
                            <div className="font-serif-display text-xl text-[color:var(--bl-takealot-blue)]">{r.region}</div>
                            <div className="text-xs text-[color:var(--bl-text-dim)] font-mono-num">{r.skus || (r.ok + r.low + r.oos + r.over + r.idle + r.stale)} SKUs</div>
                        </div>
                        <div className="mt-1 font-serif-display text-2xl font-mono-num">{formatInt(r.qty)}</div>
                        <div className="text-xs text-[color:var(--bl-text-dim)]">{formatKg(r.kg)} · {formatZAR(r.value)} value</div>
                        {r.in_transit > 0 && (
                            <div className="mt-1 text-xs inline-flex items-center gap-1 font-mono-num text-[color:var(--bl-takealot-blue)]">
                                <Truck className="w-3 h-3" /> +{formatInt(r.in_transit)} on their way → effective {formatInt(r.qty + r.in_transit)}
                            </div>
                        )}
                        <div className="mt-2 flex gap-1 flex-wrap text-[10px]">
                            <span className="px-1.5 py-0.5 rounded-sm font-mono-num" style={{ background: "rgba(192,57,43,0.12)", color: "var(--bl-danger)" }}>OOS {r.oos}</span>
                            <span className="px-1.5 py-0.5 rounded-sm font-mono-num" style={{ background: "rgba(184,118,29,0.12)", color: "var(--bl-warning)" }}>Low {r.low}</span>
                            <span className="px-1.5 py-0.5 rounded-sm font-mono-num" style={{ background: "rgba(227,90,0,0.12)", color: "var(--bl-takealot-orange)" }}>Over {r.over}</span>
                            <span className="px-1.5 py-0.5 rounded-sm font-mono-num" style={{ background: "rgba(47,122,77,0.10)", color: "var(--bl-success)" }}>OK {r.ok}</span>
                            <span className="px-1.5 py-0.5 rounded-sm font-mono-num" style={{ background: "rgba(117,97,79,0.12)", color: "var(--bl-text-dim)" }}>Sold30 {formatInt(r.sold30)}</span>
                        </div>
                    </div>
                ))}
            </div>

            {transit.shipments && transit.shipments.length > 0 && (
                <div className="mb-5 border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm" data-testid="inv-shipments-en-route">
                    <div className="px-4 py-3 border-b border-[color:var(--bl-border)] flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-[color:var(--bl-takealot-blue)]" />
                            <div>
                                <div className="font-serif-display text-lg">Shipments on their way</div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)]">
                                    Sent to Takealot but not yet fully received — deducted from your reorder needs
                                </div>
                            </div>
                        </div>
                        <div className="text-xs text-[color:var(--bl-text-dim)] font-mono-num">{transit.total_qty} units · {transit.shipments.length} shipments</div>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] bg-[color:var(--bl-bg-2)]/40">
                            <tr>
                                <th className="text-left px-4 py-2">PO</th>
                                <th className="text-left px-3 py-2">Destination</th>
                                <th className="text-left px-3 py-2">Due</th>
                                <th className="text-left px-3 py-2">State</th>
                                <th className="text-left px-3 py-2">Items</th>
                                <th className="text-right px-3 py-2">Units</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transit.shipments.map((s) => (
                                <tr key={s.shipment_id} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/40" data-testid={`shipment-row-${s.shipment_id}`}>
                                    <td className="px-4 py-2 font-mono-num text-xs">
                                        <div>#{s.purchase_order_number}</div>
                                        <div className="text-[10px] text-[color:var(--bl-text-dim)]">{s.reference}</div>
                                    </td>
                                    <td className="px-3 py-2 text-[color:var(--bl-takealot-blue)] font-medium">{s.destination_region}</td>
                                    <td className="px-3 py-2 text-xs font-mono-num">{(s.due_date || "").slice(0, 10)}</td>
                                    <td className="px-3 py-2 text-xs">
                                        <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wider ${
                                            s.state === "shipped"
                                                ? "bg-[color:var(--bl-takealot-blue)]/12 text-[color:var(--bl-takealot-blue)]"
                                                : "bg-[color:var(--bl-warning)]/12 text-[color:var(--bl-warning)]"
                                        }`}>{s.state.replace(/_/g, " ")}</span>
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        <div className="flex flex-wrap gap-1">
                                            {s.items.slice(0, 4).map((it) => (
                                                <span key={it.sku} className="font-mono-num text-[10px] px-1.5 py-0.5 rounded-sm bg-[color:var(--bl-surface-2)] text-[color:var(--bl-text-dim)]">
                                                    {it.sku.slice(-4)}: {it.qty}
                                                </span>
                                            ))}
                                            {s.items.length > 4 && (
                                                <span className="text-[10px] text-[color:var(--bl-text-mute)]">+{s.items.length - 4}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-num font-medium">{formatInt(s.total_qty)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="flex items-center gap-2 mb-3 text-xs text-[color:var(--bl-text-dim)]">
                <span className="uppercase tracking-wider">Filter:</span>
                {["", "oos", "low", "over", "ok"].map((s) => (
                    <button key={s || "all"} data-testid={`inv-filter-${s || "all"}`} onClick={() => setFilterStatus(s)}
                        className={`px-2 py-0.5 text-xs uppercase tracking-wider rounded-sm border ${
                            filterStatus === s
                                ? "border-[color:var(--bl-takealot-blue)] bg-[color:var(--bl-takealot-blue)] text-white"
                                : "border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]"
                        }`}>{s || "all"}</button>
                ))}
                <span className="ml-4 uppercase tracking-wider">Sort:</span>
                {[
                    { k: "warning", l: "Issues first" },
                    { k: "sold30", l: "Velocity" },
                    { k: "qty", l: "Quantity" },
                    { k: "name", l: "Name" },
                ].map((s) => (
                    <button key={s.k} data-testid={`inv-sort-${s.k}`} onClick={() => setSortKey(s.k)}
                        className={`px-2 py-0.5 text-xs rounded-sm border inline-flex items-center gap-1 ${
                            sortKey === s.k ? "border-[color:var(--bl-takealot-orange)] text-[color:var(--bl-takealot-orange)]" : "border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]"
                        }`}><ArrowUpDown className="w-3 h-3" /> {s.l}</button>
                ))}
            </div>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm overflow-x-auto shadow-sm">
                <table className="w-full text-sm min-w-[1100px]">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] bg-[color:var(--bl-bg-2)]/40">
                        <tr>
                            <th className="text-left px-4 py-2">Product</th>
                            {data.regions.map((r) => (
                                <th key={r} className="text-left px-2 py-2" data-testid={`inv-header-${r}`}>{r}</th>
                            ))}
                            <th className="text-right px-2 py-2">Total</th>
                            <th className="text-right px-2 py-2">Cover</th>
                            <th className="text-right px-2 py-2">Stock value</th>
                            <th className="text-right px-2 py-2">Reorder</th>
                        </tr>
                    </thead>
                    <tbody data-testid="inv-matrix-table">
                        {filtered.length === 0 && (
                            <tr><td colSpan={data.regions.length + 5} className="text-center text-[color:var(--bl-text-dim)] py-6">No products match.</td></tr>
                        )}
                        {filtered.map((row) => (
                            <tr key={row.sku} data-testid={`inv-row-${row.sku}`} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/30">
                                <td className="px-4 py-3 align-top">
                                    <Link to={`/products/${encodeURIComponent(row.sku)}`} className="flex items-start gap-3 hover:opacity-80">
                                        {row.image_url ? (
                                            <img src={row.image_url} alt="" className="w-10 h-10 object-cover rounded-sm border border-[color:var(--bl-border)] bg-white" />
                                        ) : (
                                            <div className="w-10 h-10 bg-[color:var(--bl-bg-2)] rounded-sm" />
                                        )}
                                        <div className="min-w-0">
                                            <div className="text-[color:var(--bl-text)] line-clamp-2 leading-snug">{row.title}</div>
                                            <div className="font-mono-num text-[10px] text-[color:var(--bl-text-dim)] mt-0.5">{row.sku}</div>
                                            <div className="text-[10px] text-[color:var(--bl-text-dim)] mt-0.5">{formatZAR(row.selling_price)} · {row.kg_per_unit ? `${row.kg_per_unit} kg/unit` : "kg/unit not set"}</div>
                                            {row.warning_count > 0 && (
                                                <div className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[color:var(--bl-warning)]/15 text-[color:var(--bl-warning)]">
                                                    <AlertTriangle className="w-3 h-3" /> {row.warning_count} issue{row.warning_count > 1 ? "s" : ""}
                                                </div>
                                            )}
                                            {(row.replen_blocks || []).length > 0 && (
                                                <div className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[color:var(--bl-danger)]/15 text-[color:var(--bl-danger)]">
                                                    <Ban className="w-3 h-3" /> Replen blocked
                                                </div>
                                            )}
                                        </div>
                                    </Link>
                                </td>
                                {data.regions.map((region) => (
                                    <RegionCell key={region} region={region} data={row.regions[region]} />
                                ))}
                                <td className="px-2 py-3 text-right align-top font-mono-num">
                                    <div className="text-[color:var(--bl-text)]">{formatInt(row.total_qty)}</div>
                                    <div className="text-[10px] text-[color:var(--bl-text-dim)]">{formatKg(row.total_kg)}</div>
                                    {row.total_in_transit > 0 && (
                                        <div className="text-[10px] mt-0.5 inline-flex items-center gap-0.5 text-[color:var(--bl-takealot-blue)]">
                                            <Truck className="w-3 h-3" /> +{formatInt(row.total_in_transit)} → {formatInt(row.total_effective)}
                                        </div>
                                    )}
                                    {row.seller_wh_qty > 0 && <div className="text-[10px] text-[color:var(--bl-text-dim)]">+{row.seller_wh_qty} at seller WH</div>}
                                </td>
                                <td className="px-2 py-3 text-right align-top font-mono-num text-xs">
                                    {row.cover_days_overall != null ? `${row.cover_days_overall}d` : "—"}
                                </td>
                                <td className="px-2 py-3 text-right align-top font-mono-num">
                                    <div>{formatZAR(row.total_value)}</div>
                                    {row.cost != null && <div className="text-[10px] text-[color:var(--bl-text-dim)]">cost {formatZAR(row.total_cost_value)}</div>}
                                </td>
                                <td className="px-2 py-3 align-top">
                                    {row.recommendations.length === 0 ? (
                                        <span className="text-xs text-[color:var(--bl-text-mute)]">—</span>
                                    ) : (
                                        <div className="flex flex-col gap-0.5">
                                            {row.recommendations.map((rec) => (
                                                <span key={rec.region} className="text-xs font-mono-num inline-flex items-center gap-1 text-[color:var(--bl-takealot-orange)]">
                                                    <ArrowDownToLine className="w-3 h-3" />
                                                    {rec.region}: {rec.qty}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 text-[10px] text-[color:var(--bl-text-dim)] flex gap-4 flex-wrap">
                <span>Legend:</span>
                {Object.entries(STATUS_META).map(([k, m]) => (
                    <span key={k} className="inline-flex items-center gap-1">
                        <span className="w-3 h-3 rounded-sm inline-block" style={{ background: m.bg, border: `1px solid ${m.color}` }} />
                        {m.label}
                    </span>
                ))}
                <span className="ml-auto">Reorder = max(0, sold30 × 1.5 − qty − in-transit), shown when effective stock &lt; 50% of 30-day sell-through</span>
            </div>
        </div>
    );
}
